import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';

const G     = '#1A6B3C';
const LIGHT = '#EAF3DE';

const STAR_NAMES = ['Beginner','Active Farmer','Growing Farmer','Influencer Farmer','Leader Farmer','Top Farmer','Elite Farmer'];

const AVATARS = [
  { id:'farmer_m', emoji:'🧑‍🌾', label:'Farmer' },
  { id:'farmer_f', emoji:'👩‍🌾', label:'Farmer' },
  { id:'boy',      emoji:'👨',   label:'Man' },
  { id:'girl',     emoji:'👩',   label:'Woman' },
  { id:'old_m',    emoji:'👴',   label:'Elder' },
  { id:'old_f',    emoji:'👵',   label:'Elder' },
  { id:'young_m',  emoji:'🧑',   label:'Youth' },
  { id:'young_f',  emoji:'👧',   label:'Youth' },
  { id:'turban',   emoji:'👳',   label:'Turban' },
  { id:'hat',      emoji:'🤠',   label:'Hat' },
  { id:'robot',    emoji:'🤖',   label:'Robot' },
  { id:'alien',    emoji:'👽',   label:'Alien' },
];

export const AVATAR_MAP = Object.fromEntries(AVATARS.map(a => [a.id, a.emoji]));

// ── Crop preference component — loads categories from DB ─────
function CropPreferences({ preferences, onChange }) {
  const [categories,  setCategories]  = useState([]);
  const [expandedCat, setExpandedCat] = useState(null);
  const [loadingCats, setLoadingCats] = useState(true);

  useEffect(() => {
    api.get('/categories')
      .then(({ data }) => setCategories((data.categories || []).filter(c => c.isActive)))
      .catch(() => {})
      .finally(() => setLoadingCats(false));
  }, []);

  const toggleCategory = (catName) => {
    const cats = preferences.categories || [];
    const hasCat = cats.includes(catName);
    let newCats, newSubs;
    if (hasCat) {
      newCats = cats.filter(c => c !== catName);
      const cat = categories.find(c => c.name === catName);
      const catSubs = (cat?.subCategories || []).map(s => s.name);
      newSubs = (preferences.subCategories || []).filter(s => !catSubs.includes(s));
    } else {
      newCats = [...cats, catName];
      newSubs = preferences.subCategories || [];
    }
    onChange({ categories: newCats, subCategories: newSubs });
  };

  const toggleSub = (sub) => {
    const subs = preferences.subCategories || [];
    const newSubs = subs.includes(sub) ? subs.filter(s => s !== sub) : [...subs, sub];
    onChange({ ...preferences, subCategories: newSubs });
  };

  const totalSelected = (preferences.categories?.length || 0) + (preferences.subCategories?.length || 0);

  if (loadingCats) return (
    <div style={{ textAlign:'center', padding:20 }}>
      <div style={{ width:22, height:22, border:'3px solid #E5E7EB', borderTopColor:G, borderRadius:'50%', animation:'spin .8s linear infinite', margin:'0 auto' }} />
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div style={{ fontSize:13, color:'#6B7280' }}>
          {totalSelected === 0 ? 'Showing all crops' : `${totalSelected} preference${totalSelected > 1 ? 's' : ''} selected`}
        </div>
        {totalSelected > 0 && (
          <button onClick={() => onChange({ categories:[], subCategories:[] })}
            style={{ fontSize:12, color:'#DC2626', background:'none', border:'none', cursor:'pointer' }}>
            ✕ Clear all
          </button>
        )}
      </div>

      {/* ── Category image grid ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(0,1fr))', gap:10, marginBottom:16 }}>
        {categories.map(cat => {
          const isSelected = (preferences.categories || []).includes(cat.name);
          return (
            <button key={cat._id} onClick={() => { toggleCategory(cat.name); setExpandedCat(expandedCat === cat._id ? null : cat._id); }}
              style={{ background:'none', border:'none', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:6, padding:4 }}>
              <div style={{ position:'relative' }}>
                <div style={{ width:72, height:72, borderRadius:'50%', overflow:'hidden',
                  border: isSelected ? `3px solid ${G}` : '3px solid #E5E7EB',
                  background:LIGHT, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {cat.image ? (
                    <img src={cat.image} alt={cat.name}
                      style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  ) : cat.icon ? (
                    <span style={{ fontSize:32 }}>{cat.icon}</span>
                  ) : null}
                </div>
                {isSelected && (
                  <div style={{ position:'absolute', bottom:0, right:0, width:22, height:22, borderRadius:'50%',
                    background:G, display:'flex', alignItems:'center', justifyContent:'center', border:'2px solid white' }}>
                    <span style={{ color:'white', fontSize:12 }}>✓</span>
                  </div>
                )}
              </div>
              <div style={{ fontSize:11, fontWeight: isSelected ? 700 : 500,
                color: isSelected ? G : '#374151', textAlign:'center', lineHeight:1.3 }}>
                {cat.name}
              </div>
              {isSelected && (
                <div style={{ fontSize:10, color:G, background:'#D1FAE5', borderRadius:10, padding:'1px 8px' }}>
                  {((preferences.subCategories || []).filter(s => (cat.subCategories||[]).some(a => a.name === s))).length > 0
                    ? `${((preferences.subCategories || []).filter(s => (cat.subCategories||[]).some(a => a.name === s))).length} types`
                    : 'All types'}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Subcategories for selected/expanded category ── */}
      {categories.filter(cat => expandedCat === cat._id).map(cat => {
        const activeSubs = (cat.subCategories || []).filter(s => s.isActive);
        if (!activeSubs.length) return null;
        return (
          <div key={cat._id} style={{ background:'#F5F7FA', borderRadius:14, padding:12, marginBottom:12,
            border:'0.5px solid #E5E7EB' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:10 }}>
              {cat.name} — Select types:
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(0,1fr))', gap:8 }}>
              {activeSubs.map(sub => {
                const isSubSelected = (preferences.subCategories || []).includes(sub.name);
                return (
                  <button key={sub._id || sub.name} onClick={() => toggleSub(sub.name)}
                    style={{ background:'none', border:'none', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ width:56, height:56, borderRadius:'50%', overflow:'hidden',
                      border: isSubSelected ? `3px solid ${G}` : '2px solid #E5E7EB',
                      background:'#F1F3EE', display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
                      {sub.image ? (
                        <img src={sub.image} alt={sub.name}
                          style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      ) : sub.icon ? (
                        <span style={{ fontSize:24 }}>{sub.icon}</span>
                      ) : null}
                      {isSubSelected && (
                        <div style={{ position:'absolute', inset:0, background:'rgba(26,107,60,0.3)',
                          display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'50%' }}>
                          <span style={{ color:'white', fontSize:16, fontWeight:700 }}>✓</span>
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize:10, fontWeight: isSubSelected ? 700 : 400,
                      color: isSubSelected ? G : '#6B7280', textAlign:'center', lineHeight:1.2 }}>
                      {sub.name}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ProfilePage() {
  const { user, logout, updateUser } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();

  const [stats, setStats]     = useState({ cropCount:0, followerCount:0, followingCount:0 });
  const [editing, setEditing] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showPreferences, setShowPreferences]   = useState(false);
  const [uploadingPhoto, setUploadingPhoto]     = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [form, setForm]       = useState({ name:'', locationAddress:'', gender:'', profileImageUrl:'', avatarId:'' });
  const [preferences, setPreferences] = useState({ categories:[], subCategories:[] });
  const [pwaInstalled, setPwaInstalled] = useState(false);
  const [canInstallPrompt, setCanInstallPrompt] = useState(false);

  // ── PWA install state (shares the prompt captured in index.js) ──
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setPwaInstalled(!!standalone);
    if (window.__pwaInstallPrompt) setCanInstallPrompt(true);

    const onAvailable = () => setCanInstallPrompt(true);
    const onInstalled = () => { setPwaInstalled(true); setCanInstallPrompt(false); };
    window.addEventListener('pwaInstallAvailable', onAvailable);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('pwaInstallAvailable', onAvailable);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (pwaInstalled) { toast('App is already installed ✅'); return; }

    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    if (isIOS) {
      toast('Tap Share ⬆️ in Safari, then "Add to Home Screen"', { duration: 4000 });
      return;
    }

    const promptEvent = window.__pwaInstallPrompt;
    if (!promptEvent) {
      toast('Open your browser menu and choose "Add to Home Screen" / "Install App"', { duration: 4000 });
      return;
    }

    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === 'accepted') {
      toast.success('App installed ✅');
      setPwaInstalled(true);
    }
    window.__pwaInstallPrompt = null;
    setCanInstallPrompt(false);
  };

  useEffect(() => {
    if (user) {
      setForm({
        name:            user.name || '',
        locationAddress: user.locationAddress || '',
        gender:          user.gender || '',
        profileImageUrl: user.profileImageUrl || '',
        avatarId:        user.avatarId || '',
      });
      setPreferences(user.cropPreferences || { categories:[], subCategories:[] });
      api.get(`/users/${user._id}`).then(({ data }) => setStats(data.user)).catch(() => {});
    }
  }, [user]);

  const save = async () => {
    setLoading(true);
    try {
      const { data } = await api.put('/auth/profile', form);
      updateUser(data.user);
      toast.success('Profile updated ✅');
      setEditing(false);
    } catch { toast.error('Error updating profile'); }
    finally { setLoading(false); }
  };

  const savePreferences = async (prefs) => {
    setSavingPrefs(true);
    try {
      const { data } = await api.put('/auth/profile', { cropPreferences: prefs });
      updateUser(data.user);
      toast.success('Preferences saved ✅');
    } catch { toast.error('Error saving preferences'); }
    finally { setSavingPrefs(false); }
  };

  const handlePreferenceChange = (prefs) => {
    setPreferences(prefs);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = (ev) => setForm(p => ({ ...p, profileImageUrl: ev.target.result, avatarId: '' }));
      reader.readAsDataURL(file);
      const fd = new FormData();
      fd.append('files', file);
      const { data } = await api.post('/upload/media', fd);
      if (data.files?.[0]?.url) {
        const url = data.files[0].url;
        setForm(p => ({ ...p, profileImageUrl: url, avatarId: '' }));
        const { data: saved } = await api.put('/auth/profile', { profileImageUrl: url, avatarId: '' });
        updateUser(saved.user);
        toast.success('Profile photo updated! ✅');
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploadingPhoto(false); e.target.value = ''; }
  };

  const selectAvatar = async (av) => {
    setForm(p => ({ ...p, avatarId: av.id, profileImageUrl: '' }));
    setShowAvatarPicker(false);
    try {
      const { data } = await api.put('/auth/profile', { avatarId: av.id, profileImageUrl: '' });
      updateUser(data.user);
    } catch {}
  };

  const shareReferral = () => {
    const domain = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    const url = `${domain}/#/login?ref=${user.referralCode}`;
    if (navigator.share) navigator.share({ title:'Join Zeva2', text:`Use my referral code ${user.referralCode}`, url });
    else { navigator.clipboard?.writeText(url); toast.success('Referral link copied!'); }
  };

  const getAvatar = () => {
    const imgUrl = form.profileImageUrl;
    if (imgUrl && !imgUrl.startsWith('data:')) return { type:'img', src: imgUrl };
    const av = AVATARS.find(a => a.id === form.avatarId);
    if (av) return { type:'emoji', emoji: av.emoji };
    return { type:'letter', letter: user?.name?.[0]?.toUpperCase() || '👤' };
  };

  if (!user) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'80vh', gap:12, fontFamily:'Inter,system-ui,sans-serif' }}>
      <div style={{ fontSize:52 }}>👤</div>
      <h2 style={{ fontSize:18, fontWeight:700, color:'#1a1a1a' }}>Login to view your profile</h2>
      <button onClick={() => navigate('/login')}
        style={{ background:G, color:'white', border:'none', borderRadius:20, padding:'11px 22px', fontSize:13.5, fontWeight:700, cursor:'pointer' }}>
        Login / Register
      </button>
    </div>
  );

  const av = getAvatar();
  const starLevel = user.starLevel || 0;
  const prefCount = (preferences.categories?.length || 0) + (preferences.subCategories?.length || 0);
  const showPrefMenu = user.role === 'buyer' || user.role === 'agent';

  return (
    <div style={{ background:'#F5F7FA', minHeight:'100vh', fontFamily:'Inter,system-ui,sans-serif', paddingBottom:90 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ background:G, padding:'16px 16px 60px', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-10, right:-10, fontSize:110, opacity:.15 }}></div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', position:'relative', zIndex:1 }}>
          <div>
            {/* <div style={{ fontSize:12.5, color:'rgba(255,255,255,.8)' }}>My Account</div> */}
            <div style={{ fontSize:22, fontWeight:800, color:'white', marginTop:2 }}>{user.name || 'Profile'}</div>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => navigate('/notifications')}
              style={{ position:'relative', width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,.18)', border:'1px solid rgba(255,255,255,.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, cursor:'pointer' }}>
              🔔
              {unreadCount > 0 && (
                <span style={{ position:'absolute', top:-3, right:-3, background:'#F59E0B', color:'white', borderRadius:'50%', width:16, height:16, fontSize:9.5, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, border:'2px solid '+G }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button onClick={() => navigate('/search')}
              style={{ width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,.18)', border:'1px solid rgba(255,255,255,.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, cursor:'pointer' }}>
              🔍
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding:'0 14px', maxWidth:480, margin:'0 auto' }}>

        {/* Profile card */}
        <div style={{ background:'white', borderRadius:20, boxShadow:'0 6px 20px rgba(0,0,0,.08)', padding:18, marginTop:-46, marginBottom:16, position:'relative' }}>
          {!editing && (
            <button onClick={() => setEditing(true)}
              style={{ position:'absolute', top:14, right:14, background:'white', border:`1.5px solid ${G}`, color:G, borderRadius:10, padding:'6px 11px', fontSize:11.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:4, whiteSpace:'nowrap', zIndex:1 }}>
              ✏️ Edit
            </button>
          )}
          <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
            <div style={{ position:'relative', flexShrink:0 }}>
              <div style={{ width:72, height:72, borderRadius:'50%', background:LIGHT, color:G, display:'flex', alignItems:'center', justifyContent:'center', fontSize: av.type==='emoji' ? 32 : 26, fontWeight:800, overflow:'hidden' }}>
                {av.type === 'img'    && <img src={av.src} alt="profile" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => e.target.style.display='none'} />}
                {av.type === 'emoji'  && av.emoji}
                {av.type === 'letter' && av.letter}
              </div>
              {editing && (
                <div style={{ position:'absolute', bottom:-2, right:-2, display:'flex', gap:4 }}>
                  <label style={{ width:24, height:24, borderRadius:'50%', background:G, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:11, border:'2px solid white' }}>
                    {uploadingPhoto ? '…' : '📷'}
                    <input type="file" accept="image/*" capture="environment" style={{ display:'none' }} onChange={handlePhotoUpload} />
                  </label>
                  <button onClick={() => setShowAvatarPicker(true)}
                    style={{ width:24, height:24, borderRadius:'50%', background:'#7C3AED', border:'2px solid white', fontSize:11, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                    🎭
                  </button>
                </div>
              )}
            </div>

            {!editing ? (
              <div style={{ flex:1, minWidth:0, paddingRight:64 }}>
                <div style={{ fontSize:17, fontWeight:800, color:'#1a1a1a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {user.name || 'Complete your profile'}
                </div>
                <div style={{ display:'inline-flex', alignItems:'center', gap:4, background:LIGHT, color:G, fontSize:11.5, fontWeight:700, padding:'3px 10px', borderRadius:20, marginTop:6, textTransform:'capitalize', maxWidth:'100%' }}>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}> {user.role} · {user.locationAddress || 'Location not set'}</span>
                </div>
                <div style={{ fontSize:12.5, color:'#4B5D4F', marginTop:6, display:'flex', alignItems:'center', gap:5 }}>
                  📱 +91 {user.mobile}
                </div>
                <div style={{ marginTop:6, display:'flex', alignItems:'center', gap:6 }}>
                  {Array.from({ length:5 }).map((_, i) => (
                    <span key={i} style={{ color: i < starLevel ? '#F59E0B' : '#E5E7EB', fontSize:15 }}>★</span>
                  ))}
                  <span style={{ fontSize:12, color:'#9CA3AF' }}>{STAR_NAMES[Math.min(starLevel,6)]}</span>
                </div>
              </div>
            ) : (
              <div style={{ flex:1, minWidth:0, textAlign:'left', display:'flex', flexDirection:'column', gap:10 }}>
                <div>
                  <label style={{ fontSize:11.5, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4 }}>Full Name</label>
                  <input value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} placeholder="Your name"
                    style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none' }} />
                </div>
                <div>
                  <label style={{ fontSize:11.5, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4 }}>Location</label>
                  <input value={form.locationAddress} onChange={e => setForm(p=>({...p,locationAddress:e.target.value}))} placeholder="Village, District, State"
                    style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none' }} />
                </div>
                <div>
                  <label style={{ fontSize:11.5, fontWeight:600, color:'#6B7280', display:'block', marginBottom:4 }}>Gender</label>
                  <select value={form.gender} onChange={e => setForm(p=>({...p,gender:e.target.value}))}
                    style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none' }}>
                    <option value="">Select</option>
                    <option value="male">👨 Male</option>
                    <option value="female">👩 Female</option>
                    <option value="other">🧑 Other</option>
                    <option value="not_specified">🤐 Prefer not to say</option>
                  </select>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={save} disabled={loading}
                    style={{ flex:1, background:G, color:'white', border:'none', borderRadius:12, padding:11, fontSize:13, fontWeight:700, cursor:'pointer' }}>
                    {loading ? '…' : '✅ Save Changes'}
                  </button>
                  <button onClick={() => setEditing(false)}
                    style={{ flex:1, background:'#F1F3EE', color:'#4B5563', border:'none', borderRadius:12, padding:11, fontSize:13, fontWeight:700, cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {!editing && (
            <div style={{ display:'flex', marginTop:16, paddingTop:16, borderTop:'0.5px solid #F1F3EE' }}>
              {[
                { label:'Crops',     value: stats.cropCount||0,      icon:'', bg:LIGHT,     color:G },
                { label:'Followers', value: stats.followerCount||0,  icon:'👥', bg:'#F3E8FF', color:'#7C3AED' },
                { label:'Following', value: stats.followingCount||0, icon:'➕', bg:'#DBEAFE', color:'#2563EB' },
              ].map((s, i) => (
                <React.Fragment key={s.label}>
                  <div style={{ flex:1, textAlign:'center' }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', background:s.bg, color:s.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, margin:'0 auto 6px' }}>{s.icon}</div>
                    <div style={{ fontSize:17, fontWeight:800, color:'#1a1a1a' }}>{s.value}</div>
                    <div style={{ fontSize:11, color:'#9CA3AF' }}>{s.label}</div>
                  </div>
                  {i < 2 && <div style={{ width:'0.5px', background:'#F1F3EE' }} />}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* ── Crop Preferences (buyers & agents only) ── */}
        {showPrefMenu && (
          <div style={{ background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', padding:16, marginBottom:14 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, gap:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:LIGHT, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}></div>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontWeight:800, fontSize:14, color:'#1a1a1a' }}>Crop Preferences</div>
                  <div style={{ fontSize:11.5, color:'#9CA3AF', marginTop:1 }}>
                    {prefCount === 0
                      ? 'Showing all crops — set preferences to filter'
                      : `${prefCount} preference${prefCount > 1 ? 's' : ''} active`}
                  </div>
                </div>
              </div>
              <button onClick={() => setShowPreferences(!showPreferences)}
                style={{ background:'white', border:`1.5px solid ${G}`, color:G, borderRadius:12, padding:'7px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', gap:4 }}>
                {showPreferences ? 'Hide' : '✏️ Edit'} {!showPreferences && '›'}
              </button>
            </div>

            {/* Active preference chips */}
            {prefCount > 0 && !showPreferences && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {(preferences.categories || []).map(c => (
                  <span key={c} style={{ background:'#D1FAE5', color:'#065F46', borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:600, textTransform:'capitalize' }}>
                    {c}
                  </span>
                ))}
                {(preferences.subCategories || []).map(s => (
                  <span key={s} style={{ background:'#DBEAFE', color:'#1D4ED8', borderRadius:20, padding:'3px 10px', fontSize:12 }}>
                    {s}
                  </span>
                ))}
              </div>
            )}

            {showPreferences && (
              <>
                <CropPreferences preferences={preferences} onChange={handlePreferenceChange} />
                <div style={{ display:'flex', gap:8, marginTop:14 }}>
                  <button onClick={() => { savePreferences(preferences); setShowPreferences(false); }} disabled={savingPrefs}
                    style={{ flex:1, background:G, color:'white', border:'none', borderRadius:12, padding:11, fontSize:13, fontWeight:700, cursor:'pointer' }}>
                    {savingPrefs ? '…' : '✅ Save Preferences'}
                  </button>
                  <button onClick={() => setShowPreferences(false)}
                    style={{ flex:1, background:'white', color:G, border:`1.5px solid ${G}`, borderRadius:12, padding:11, fontSize:13, fontWeight:700, cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Referral code */}
        <div style={{ background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', padding:16, marginBottom:14 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:'#FEF3C7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>🎁</div>
            <div style={{ fontWeight:800, fontSize:14, color:'#1a1a1a' }}>Referral Code</div>
          </div>
          <div style={{ border:`1.5px dashed ${G}`, borderRadius:12, padding:'12px', textAlign:'center', fontSize:19, fontWeight:800, letterSpacing:4, color:G, marginBottom:10 }}>
            {(user.referralCode || 'N/A').slice(0,8)}
          </div>
          <div style={{ fontSize:11.5, color:'#2563EB', textAlign:'center', marginBottom:12, wordBreak:'break-all' }}>
            {(process.env.REACT_APP_FRONTEND_URL || 'https://zevazeva.com')}/#/login?ref={user.referralCode}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={shareReferral}
              style={{ flex:1, background:G, color:'white', border:'none', borderRadius:12, padding:11, fontSize:12.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              🔗 Share Link
            </button>
            <button onClick={() => { navigator.clipboard?.writeText(user.referralCode); toast.success('Copied!'); }}
              style={{ flex:1, background:'white', color:G, border:`1.5px solid ${G}`, borderRadius:12, padding:11, fontSize:12.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              📋 Copy
            </button>
          </div>
          <p style={{ fontSize:11.5, color:'#9CA3AF', textAlign:'center', marginTop:10 }}>{user.referralCount||0} friends joined</p>
        </div>

        {/* Install app banner */}
        {!pwaInstalled && (
          <div onClick={handleInstallClick}
            style={{ background:LIGHT, borderRadius:16, padding:'14px 16px', display:'flex', alignItems:'center', gap:12, cursor:'pointer', marginBottom:14 }}>
            <div style={{ width:40, height:40, borderRadius:12, background:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>📲</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#1a1a1a' }}>Install App on Mobile</div>
              <div style={{ fontSize:11, color:'#4B5D4F', marginTop:2 }}>
                {canInstallPrompt ? 'Tap here to install' : 'Tap browser menu → "Add to Home Screen"'}
              </div>
            </div>
            <button onClick={e => { e.stopPropagation(); handleInstallClick(); }}
              style={{ background:G, color:'white', border:'none', borderRadius:12, padding:'9px 14px', fontSize:11.5, fontWeight:700, cursor:'pointer', flexShrink:0 }}>
              Install Now
            </button>
          </div>
        )}

        {/* Menu */}
        <div style={{ background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', marginBottom:20, overflow:'hidden' }}>
          {user.role === 'agent'  && <MenuRow icon="🤝" label="Agent Dashboard" bg="#F3E8FF" color="#7C3AED" onClick={() => navigate('/agent')} />}
          <MenuRow icon="🔍" label="Search" bg={LIGHT} color={G} onClick={() => navigate('/search')} />
          <MenuRow icon="🔔" label={`Notifications${unreadCount > 0 ? ` (${unreadCount})` : ''}`} bg="#FEF3C7" color="#D97706" onClick={() => navigate('/notifications')} />
          <MenuRow icon="🎧" label="Support" bg="#F3E8FF" color="#7C3AED" onClick={() => navigate('/support')} />
          <MenuRow icon="🚪" label="Logout" bg="#FEE2E2" color="#DC2626" danger onClick={() => { logout(); navigate('/login'); }} last />
        </div>
      </div>

      {showAvatarPicker && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:200, display:'flex', alignItems:'flex-end' }}
          onClick={() => setShowAvatarPicker(false)}>
          <div style={{ background:'white', borderRadius:'20px 20px 0 0', padding:20, width:'100%', maxWidth:480, margin:'0 auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:800, fontSize:16, marginBottom:16, textAlign:'center', color:'#1a1a1a' }}>Choose Avatar</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, minmax(0,1fr))', gap:10, marginBottom:16 }}>
              {AVATARS.map(av => (
                <button key={av.id} onClick={() => selectAvatar(av)}
                  style={{ background: form.avatarId===av.id ? LIGHT : '#F5F7FA', border: form.avatarId===av.id ? `2px solid ${G}` : '2px solid #E5E7EB', borderRadius:12, padding:10, display:'flex', flexDirection:'column', alignItems:'center', gap:4, cursor:'pointer' }}>
                  <span style={{ fontSize:32 }}>{av.emoji}</span>
                  <span style={{ fontSize:11, color:'#6B7280' }}>{av.label}</span>
                </button>
              ))}
            </div>
            <label style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, cursor:'pointer', width:'100%', background:'white', border:`1.5px solid ${G}`, color:G, borderRadius:12, padding:12, fontSize:13, fontWeight:700 }}>
              📷 Upload Real Photo
              <input type="file" accept="image/*" style={{ display:'none' }} onChange={e => { handlePhotoUpload(e); setShowAvatarPicker(false); }} />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuRow({ icon, label, onClick, danger, bg, color, last }) {
  return (
    <button onClick={onClick}
      style={{
        width:'100%', display:'flex', alignItems:'center', gap:12, padding:'13px 16px',
        background:'none', border:'none', borderBottom: last ? 'none' : '0.5px solid #F1F3EE',
        cursor:'pointer', textAlign:'left',
      }}>
      <span style={{ width:34, height:34, borderRadius:'50%', background:bg, color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, flexShrink:0 }}>{icon}</span>
      <span style={{ flex:1, fontSize:14, fontWeight:600, color: danger ? '#DC2626' : '#1a1a1a' }}>{label}</span>
      {!danger && <span style={{ color:'#D1D5DB', fontSize:16 }}>›</span>}
    </button>
  );
}